import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import EmailVerifiedPage from "@/pages/EmailVerifiedPage";
import ProfilePage from "@/pages/ProfilePage";
import RuleDiffPreviewPage from "@/pages/RuleDiffPreviewPage";
import RuleDiffBrowsePage from "@/pages/RuleDiffBrowsePage";
import DashboardPage from "@/pages/DashboardPage";
import ExamPage from "@/pages/ExamPage";
import ExamHistoryPage from "@/pages/ExamHistoryPage";
import ExamAttemptDetailPage from "@/pages/ExamAttemptDetailPage";
import OnboardingPage from "@/pages/OnboardingPage";
import PracticePage from "@/pages/PracticePage";
import TrainingPage from "@/pages/TrainingPage";
import LessonPage from "@/pages/LessonPage";
import CoursesPage from "@/pages/CoursesPage";
import CoursePage from "@/pages/CoursePage";
import ApplyPage from "@/pages/ApplyPage";
import WhatsNextPage from "@/pages/WhatsNextPage";
import BlogPage from "@/pages/BlogPage";
import SupportPage from "@/pages/SupportPage";
import ChecklistsPage from "@/pages/ChecklistsPage";
// ADR-0013 Phase 1B (ROADS-477) — admin page imports moved to AdminApp.tsx
// so the learner bundle no longer ships admin code. See `src/AdminApp.tsx`.
import OfflinePage from "@/pwa/OfflinePage";
import InstallPrompt from "@/pwa/InstallPrompt";
import TopNav from "@/components/TopNav";
import ProtectedRoute from "@/components/ProtectedRoute";
import OnboardingGate from "@/components/OnboardingGate";
import HomeRedirectGate from "@/components/HomeRedirectGate";
import ErrorBoundary from "@/components/ErrorBoundary";
import PaidSurfaceGate from "@/components/paywall/PaidSurfaceGate";
import { EntitlementGateProvider } from "@/entitlement/EntitlementGateContext";
import RequiresEntitlement from "@/entitlement/RequiresEntitlement";
import CheckoutPage from "@/pages/CheckoutPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import CheckoutCancelPage from "@/pages/CheckoutCancelPage";
import CdlLicensePage from "@/pages/cdl/CdlLicensePage";
import CdlPracticePage from "@/pages/cdl/CdlPracticePage";
import CdlExamPage from "@/pages/cdl/CdlExamPage";
import PassApp from "@/pass/PassApp";
import { useLanguageSync } from "@/hooks/useLanguageSync";

/**
 * Public routes that render with `<TopNav />`. Everything else uses a
 * portal shell (LearnerPortalShell for authenticated learner pages,
 * AdminPortalShell for `/admin/*`) which provides its own chrome.
 *
 * Public surfaces:
 * - `/` — HomeRedirectGate: anonymous → /login, authenticated+onboarded → /dashboard
 * - `/about` — public marketing-style page
 * - `/login` — full-screen split landing/auth page (own branding, no TopNav)
 * - `/offline` — PWA offline fallback
 * - `/onboarding` — auth-required but pre-profile; shell would render with
 *   no user data, so we keep the public TopNav until profile completes
 *
 * All authenticated + onboarded routes (`/dashboard`, `/practice`,
 * `/profile`, `/training`, `/training/:lessonId`, `/exam/history`,
 * `/exam/history/:attemptId`, `/exam/:stateCode`, `/rule-diff/browse`,
 * `/rule-diff/preview`) and `/admin/*` get their own shell, so TopNav
 * is suppressed.
 *
 * Entitlement gating (PROFILE-PAYWALL feature):
 *
 *   - `<EntitlementGateProvider>` mounts INSIDE `<ProtectedRoute>` +
 *     `<OnboardingGate>` so it only fetches /api/entitlements/me for
 *     authenticated, onboarded users.
 *   - `<RequiresEntitlement>` wraps every paid surface; on isExpired it
 *     redirects to /dashboard (where DashboardPage swaps to
 *     `<TrialExpiredPaywall>`).
 *   - `/profile` and `/dashboard` are the ONLY routes reachable when the
 *     trial has expired — they're the conversion funnel.
 *   - `/settings` and `/settings/access` redirect to `/profile` so saved
 *     deep links still resolve.
 */
// Routes where the global TopNav (Home/About + Language + Sign In) renders.
//
// Deliberately excluded:
//   - `/login`                   LoginPage renders its own dual-panel chrome.
//   - `/onboarding`              ROADS-510 — UserMenu links would silently
//                                bounce off OnboardingGate; the page renders
//                                its own LanguageSwitcher + Sign Out.
//   - `/forgot-password`         \
//   - `/reset-password`          ROADS-512 — each page renders its own
//   - `/email-verified`          /  minimal Logo + LanguageSwitcher chrome.
//                                Letting TopNav render on top of that stacked
//                                two logos + two language pickers vertically.
const PUBLIC_ROUTES: ReadonlySet<string> = new Set([
  "/",
  "/about",
  "/offline",
]);

function shouldRenderTopNav(pathname: string): boolean {
  return PUBLIC_ROUTES.has(pathname);
}

/**
 * App shell — public chrome + the tiered route matrix:
 *
 *   PUBLIC:              /, /about, /login, /offline
 *   AUTH-GATED:          /onboarding (reachable before profile is complete)
 *   AUTH + ONBOARDED:    /settings, /rule-diff/preview
 *
 * `<OnboardingGate>` wraps post-login routes that require a completed
 * profile. `/onboarding` itself MUST NOT be inside the gate — that would
 * produce an infinite redirect loop. `/login` sits outside both the auth
 * wrapper and the onboarding gate because anonymous visitors hit it from
 * the redirect triggered by `<ProtectedRoute />`.
 *
 * Header + brand treatment live in `@/components/TopNav`.
 */
export default function App() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  useLanguageSync();
  const isOffline = pathname === "/offline";

  return (
    <div className="min-h-screen flex flex-col bg-brand-surface text-brand-primary font-sans antialiased">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:rounded-md focus:bg-brand-primary focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:no-underline"
      >
        {t("a11y.skipToMain")}
      </a>
      {shouldRenderTopNav(pathname) && <TopNav />}
      <main
        id="main-content"
        tabIndex={-1}
        className={`flex-1 ${isOffline ? "mx-auto w-full max-w-3xl px-md py-lg sm:px-lg" : ""}`}
      >
        <ErrorBoundary>
        {/*
         * key={i18n.language} forces the entire Routes subtree to unmount
         * and remount whenever the user switches UI language. Many pages
         * (LessonPage, TrainingPage, PracticePage, etc.) cache server-
         * resolved content in local state and don't track i18n.language
         * in their fetch effect's dep array; without this remount, the
         * UI chrome flips locales while lesson bodies, country diffs,
         * and practice questions stay stuck in the previous language.
         * The remount is cheap and only fires on the deliberate
         * language-switch action.
         */}
        <Routes key={i18n.language}>
          <Route path="/about" element={<AboutPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/email-verified" element={<EmailVerifiedPage />} />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route path="/offline" element={<OfflinePage />} />
          <Route
            element={
              <ProtectedRoute>
                <OnboardingGate>
                  <EntitlementGateProvider>
                    <Outlet />
                  </EntitlementGateProvider>
                </OnboardingGate>
              </ProtectedRoute>
            }
          >
            {/*
              Profile is the canonical learner-account surface. /settings
              and /settings/access redirect here so any deep link / saved
              bookmark from before this PR keeps working.
              IMPORTANT: /profile is reachable EVEN WHEN entitlements are
              expired — it's the only route besides /dashboard that the
              expired-trial user can navigate to without hitting the
              RequiresEntitlement bounce.
            */}
            <Route path="/profile" element={<ProfilePage />} />
            <Route
              path="/settings"
              element={<Navigate to="/profile" replace />}
            />
            <Route
              path="/settings/access"
              element={<Navigate to="/profile" replace />}
            />
            <Route
              path="/rule-diff/preview"
              element={
                <RequiresEntitlement>
                  <PaidSurfaceGate tier="class_c">
                    <RuleDiffPreviewPage />
                  </PaidSurfaceGate>
                </RequiresEntitlement>
              }
            />
            <Route
              path="/rule-diff/browse"
              element={
                <RequiresEntitlement>
                  <PaidSurfaceGate tier="class_c">
                    <RuleDiffBrowsePage />
                  </PaidSurfaceGate>
                </RequiresEntitlement>
              }
            />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route
              path="/practice"
              element={
                <RequiresEntitlement>
                  <PracticePage />
                </RequiresEntitlement>
              }
            />
            <Route
              path="/training"
              element={
                <RequiresEntitlement>
                  <PaidSurfaceGate tier="class_c">
                    <TrainingPage />
                  </PaidSurfaceGate>
                </RequiresEntitlement>
              }
            />
            <Route
              path="/courses"
              element={
                <RequiresEntitlement>
                  <PaidSurfaceGate tier="class_c">
                    <CoursesPage />
                  </PaidSurfaceGate>
                </RequiresEntitlement>
              }
            />
            <Route
              path="/courses/:courseId"
              element={
                <RequiresEntitlement>
                  <PaidSurfaceGate tier="class_c">
                    <CoursePage />
                  </PaidSurfaceGate>
                </RequiresEntitlement>
              }
            />
            <Route
              path="/training/:lessonId"
              element={
                <RequiresEntitlement>
                  <PaidSurfaceGate tier="class_c">
                    <LessonPage />
                  </PaidSurfaceGate>
                </RequiresEntitlement>
              }
            />
            <Route path="/apply" element={<ApplyPage />} />
            <Route path="/apply/:stateCode" element={<ApplyPage />} />
            <Route path="/whats-next" element={<WhatsNextPage />} />
            {/*
              Informational learner surfaces (nav restructure). Auth +
              onboarding gated like the rest of the portal, but NOT behind
              <RequiresEntitlement> — Blog/Support/Checklists are
              always-reachable, mirroring /profile and /dashboard so an
              expired-trial user can still read them. Checklists is the
              Courses-group child; Blog/Support are top-level nav links.
            */}
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/checklists" element={<ChecklistsPage />} />
            <Route path="/exam/history" element={<ExamHistoryPage />} />
            <Route
              path="/exam/history/:attemptId"
              element={<ExamAttemptDetailPage />}
            />
            <Route
              path="/exam/:stateCode"
              element={
                <RequiresEntitlement>
                  <PaidSurfaceGate tier="class_c">
                    <ExamPage />
                  </PaidSurfaceGate>
                </RequiresEntitlement>
              }
            />
            <Route path="/cdl/license/:state" element={<CdlLicensePage />} />
            <Route
              path="/cdl/practice"
              element={
                <RequiresEntitlement>
                  <PaidSurfaceGate tier="cdl">
                    <CdlPracticePage />
                  </PaidSurfaceGate>
                </RequiresEntitlement>
              }
            />
            <Route
              path="/cdl/exam"
              element={
                <RequiresEntitlement>
                  <PaidSurfaceGate tier="cdl">
                    <CdlExamPage />
                  </PaidSurfaceGate>
                </RequiresEntitlement>
              }
            />
            {/*
             * Vacationer "pass" surface (ROADS-569 SP2). Nested router
             * (`PassApp`) mounts the pack-home + Wave-1 screens, gated
             * by the tourist_out tier. The `/*` splat lets PassApp own
             * its sub-routes (rules / plan / learn / emergency / waitlist).
             */}
            <Route
              path="/pass/*"
              element={
                <RequiresEntitlement>
                  <PaidSurfaceGate tier="tourist_out">
                    <PassApp />
                  </PaidSurfaceGate>
                </RequiresEntitlement>
              }
            />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route
              path="/checkout/success"
              element={<CheckoutSuccessPage />}
            />
            <Route
              path="/checkout/cancel"
              element={<CheckoutCancelPage />}
            />
            {/*
             * Admin routes (ROADS-477 Phase 1B) live in AdminApp.tsx, served
             * from admin.staging.roadbridge.app via the roadbridge-admin-staging
             * Kamal service. Hitting /admin/* on the learner origin falls
             * through to the 404 below — operators land here only via the
             * Administration link in LearnerPortalShell which does a full
             * cross-origin redirect to VITE_ADMIN_URL.
             */}
          </Route>
          <Route
            path="/"
            element={
              <HomeRedirectGate>
                <HomePage />
              </HomeRedirectGate>
            }
          />
        </Routes>
        </ErrorBoundary>
      </main>
      <InstallPrompt />
    </div>
  );
}
